import { useCallback, useEffect, useState } from "react";
import { Lightning, ArrowRight, Sparkle, GlobeHemisphereWest, ShieldCheck, FileCsv, FileZip, Tag, Globe } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import AdCard from "../components/AdCard";
import ABLeaderboard from "../components/ABLeaderboard";
import ComplianceBadge from "../components/ComplianceBadge";
import EnforcementPanel from "../components/EnforcementPanel";
import UtmBuilderDialog from "../components/UtmBuilderDialog";
import GeoTargetingDialog from "../components/GeoTargetingDialog";
import CostEstimateBadge from "../components/CostEstimateBadge";
import { scrapeUrl, generateAds, listLinks, listPolicies, listSurveys, runComplianceCheck, exportCsvUrl, exportZipUrl, getAdSet } from "../lib/api";
import { PLATFORMS, TONES } from "../lib/constants";
import useAdSetPerformance from "../hooks/useAdSetPerformance";

export default function Generator() {
  const [url, setUrl] = useState("");
  const [scrape, setScrape] = useState(null);
  const [platforms, setPlatforms] = useState(["google", "meta", "tiktok", "instagram", "twitter", "reddit"]);
  const [tone, setTone] = useState("energetic");
  const [audience, setAudience] = useState("");
  const [genImage, setGenImage] = useState(true);
  const [scrapedImageUrl, setScrapedImageUrl] = useState("");
  const [affiliateLinkId, setAffiliateLinkId] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [compliancePolicyIds, setCompliancePolicyIds] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [surveyId, setSurveyId] = useState("");
  const [complianceReport, setComplianceReport] = useState(null);
  const [checkingCompliance, setCheckingCompliance] = useState(false);
  const [links, setLinks] = useState([]);
  const [loadingScrape, setLoadingScrape] = useState(false);
  const [loadingGen, setLoadingGen] = useState(false);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState("google");
  const [performance] = useAdSetPerformance(result?.id);
  const [utmOpen, setUtmOpen] = useState(false);
  const [geoOpen, setGeoOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listLinks()
      .then((l) => {
        if (!cancelled) setLinks(l);
      })
      .catch((err) => {
        console.warn("Failed to load affiliate links:", err?.message || err);
      });

    // Hydrate result from ?ad_set_id=… so navigation from Connections page lands
    // directly on a live results view.
    const params = new URLSearchParams(window.location.search);
    const preload = params.get("ad_set_id");
    if (preload) {
      getAdSet(preload)
        .then((r) => {
          if (!cancelled) {
            setResult(r);
            setScrape(r.scrape || null);
            setActiveTab(r.ads?.[0]?.platform || "google");
            if (r.compliance_report) setComplianceReport(r.compliance_report);
            if (r.compliance_policy_ids?.length) setCompliancePolicyIds(r.compliance_policy_ids);
          }
        })
        .catch(() => {});
    }
    listPolicies()
      .then((p) => {
        if (!cancelled) setPolicies(p);
      })
      .catch((err) => {
        console.warn("Failed to load policies:", err?.message || err);
      });
    listSurveys()
      .then((s) => {
        if (!cancelled) setSurveys(s);
      })
      .catch((err) => {
        console.warn("Failed to load surveys:", err?.message || err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const togglePlatform = useCallback((id) => {
    setPlatforms((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  }, []);

  const togglePolicy = useCallback((id) => {
    setCompliancePolicyIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
  }, []);

  const doComplianceCheck = useCallback(async () => {
    if (!result?.id) return;
    if (!compliancePolicyIds.length && !result.compliance_policy_ids?.length)
      return toast.error("Attach at least one policy first");
    setCheckingCompliance(true);
    try {
      const rep = await runComplianceCheck(
        result.id,
        compliancePolicyIds.length ? compliancePolicyIds : null,
      );
      setComplianceReport(rep);
      toast.success(
        `Compliance: ${rep.overall_status.toUpperCase()} · ${rep.total_violations} finding(s)`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Compliance check failed");
    } finally {
      setCheckingCompliance(false);
    }
  }, [result, compliancePolicyIds]);

  const doScrape = async () => {
    if (!url.trim()) return toast.error("Enter a URL");
    setLoadingScrape(true);
    setResult(null);
    setScrapedImageUrl("");
    try {
      const s = await scrapeUrl(url.trim());
      setScrape(s);
      // Pre-select the OG image (usually the marketing hero) if available.
      if (s.scraped_images?.length) {
        setScrapedImageUrl(s.scraped_images[0].url);
      }
      toast.success("Site scraped");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scrape failed");
    } finally {
      setLoadingScrape(false);
    }
  };

  const doGenerate = async () => {
    if (!scrape) return toast.error("Scrape the URL first");
    if (platforms.length === 0) return toast.error("Pick at least one platform");
    setLoadingGen(true);
    try {
      const res = await generateAds({
        url: scrape.url,
        scrape,
        platforms,
        tone,
        audience,
        generate_images: genImage,
        scraped_image_url: scrapedImageUrl || null,
        affiliate_link_id: affiliateLinkId || null,
        campaign_name: campaignName,
        compliance_policy_ids: compliancePolicyIds,
        survey_id: surveyId || null,
      });
      setResult(res);
      setComplianceReport(null);
      if (res.ads?.length) setActiveTab(res.ads[0].platform);
      toast.success(`Generated ${res.ads?.length || 0} ads`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setLoadingGen(false);
    }
  };

  const creatives = result?.creatives || [];

  return (
    <div className="max-w-6xl">
      <div className="mb-10">
        <div className="label-mono mb-4">01 · target</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter leading-[0.95] mb-3">
          Drop a URL. <br />
          <span className="font-black text-[#ccff00]">Print ads</span> that
          convert.
        </h1>
        <p className="text-zinc-400 max-w-xl leading-relaxed">
          Paste any product page or affiliate link. We scrape it, understand it,
          and generate high-converting ad copy + creatives for 6 platforms in
          seconds.
        </p>
      </div>

      {/* URL Input */}
      <div className="mb-8">
        <div className="relative">
          <div
            className={`absolute inset-0 pointer-events-none ${
              loadingScrape ? "pulse-glow" : ""
            }`}
          />
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <GlobeHemisphereWest
                size={20}
                className="absolute left-5 top-1/2 -translate-y-1/2 text-zinc-500"
              />
              <input
                data-testid="url-input"
                type="url"
                placeholder="https://example.com/product"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doScrape()}
                className="w-full bg-[#0a0a0a] border-2 border-white/10 text-white h-16 text-lg pl-14 pr-6 rounded-none focus:border-[#ccff00] focus:outline-none transition-colors duration-300 placeholder:text-zinc-600 font-mono"
              />
            </div>
            <button
              data-testid="scrape-btn"
              onClick={doScrape}
              disabled={loadingScrape}
              className="bg-white text-black hover:bg-zinc-200 font-semibold tracking-wide px-8 h-16 flex items-center gap-2 disabled:opacity-40 transition-colors"
            >
              {loadingScrape ? "Scraping…" : "Scrape"}
              <ArrowRight size={18} weight="bold" />
            </button>
          </div>
        </div>
      </div>

      {/* Scrape preview */}
      {scrape && (
        <div
          className="mb-6 bg-[#0a0a0a] border border-white/10 p-6 md:p-8 grid grid-cols-1 md:grid-cols-12 gap-6 fade-up"
          data-testid="scrape-preview"
        >
          <div className="md:col-span-4">
            {scrape.image ? (
              <img
                src={scrape.image}
                alt={scrape.title}
                className="w-full aspect-video object-cover border border-white/5"
                onError={(e) => (e.target.style.display = "none")}
              />
            ) : (
              <div className="w-full aspect-video bg-[#121212] border border-white/5 flex items-center justify-center text-zinc-600">
                <GlobeHemisphereWest size={40} />
              </div>
            )}
          </div>
          <div className="md:col-span-8">
            <div className="label-mono mb-2">scraped</div>
            <h3 className="font-display text-2xl font-bold leading-tight mb-2">
              {scrape.title}
            </h3>
            <p className="text-zinc-400 text-sm leading-relaxed mb-4">
              {scrape.description || "No description found."}
            </p>
            <div className="font-mono text-xs text-zinc-500 truncate">{scrape.url}</div>
            {scrape.keywords?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-4">
                {scrape.keywords.slice(0, 8).map((k, i) => (
                  <span
                    key={`${k}-${i}`}
                    className="font-mono text-[10px] px-2 py-1 bg-[#121212] border border-white/10 text-zinc-400"
                  >
                    {k}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Image picker — user chooses which scraped image to use as the ad creative */}
      {scrape?.scraped_images?.length > 0 && (
        <div
          className="mb-10 bg-[#0a0a0a] border border-white/10 p-6 fade-up"
          data-testid="scraped-image-picker"
        >
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="label-mono mb-1">use an image from the site</div>
              <div className="text-xs text-zinc-500">
                Click to select. Selected image will be used as the 1:1 hero
                creative. Deselect to skip.
              </div>
            </div>
            {scrapedImageUrl && (
              <button
                data-testid="clear-scraped-image"
                onClick={() => setScrapedImageUrl("")}
                className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 hover:text-white border border-white/10 px-3 py-1.5"
              >
                clear selection
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {scrape.scraped_images.map((img, i) => {
              const selected = scrapedImageUrl === img.url;
              return (
                <button
                  key={img.url}
                  data-testid={`scraped-image-${i}`}
                  onClick={() => setScrapedImageUrl(selected ? "" : img.url)}
                  className={`relative aspect-square border-2 overflow-hidden transition-colors ${
                    selected
                      ? "border-[#ccff00]"
                      : "border-white/10 hover:border-white/30"
                  }`}
                  title={img.alt || img.url}
                >
                  <img
                    src={img.url}
                    alt={img.alt || ""}
                    className="w-full h-full object-cover"
                    onError={(e) => (e.target.style.display = "none")}
                  />
                  {selected && (
                    <div className="absolute inset-0 bg-[#ccff00]/20 flex items-end justify-start p-2">
                      <span className="font-mono text-[9px] uppercase bg-[#ccff00] text-black px-1.5 py-0.5 font-bold">
                        selected
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Config */}
      {scrape && (
        <div className="mb-10 fade-up">
          <div className="label-mono mb-4">02 · configure</div>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
            <div className="bg-[#0a0a0a] border border-white/10 p-6">
              <div className="label-mono mb-3">platforms</div>
              <div className="grid grid-cols-2 gap-2">
                {PLATFORMS.map((p) => (
                  <label
                    key={p.id}
                    data-testid={`platform-${p.id}`}
                    className={`flex items-center gap-2 px-3 py-2 border cursor-pointer transition-colors ${
                      platforms.includes(p.id)
                        ? "border-[#ccff00] bg-[#ccff00]/5 text-[#ccff00]"
                        : "border-white/10 text-zinc-400 hover:border-white/30"
                    }`}
                  >
                    <Checkbox
                      checked={platforms.includes(p.id)}
                      onCheckedChange={() => togglePlatform(p.id)}
                      className="border-white/20 data-[state=checked]:bg-[#ccff00] data-[state=checked]:text-black rounded-none"
                    />
                    <span className="text-sm font-medium">{p.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="bg-[#0a0a0a] border border-white/10 p-6 space-y-4">
              <div>
                <div className="label-mono mb-2">tone</div>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger
                    data-testid="tone-select"
                    className="rounded-none bg-[#121212] border-white/10 h-11"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10 rounded-none">
                    {TONES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="label-mono mb-2">target audience</div>
                <Input
                  data-testid="audience-input"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  placeholder="e.g. crypto-curious millennials"
                  className="rounded-none bg-[#121212] border-white/10 h-11 font-mono text-sm"
                />
              </div>
            </div>

            <div className="bg-[#0a0a0a] border border-white/10 p-6 space-y-4">
              <div>
                <div className="label-mono mb-2">creative</div>
                <label
                  data-testid="toggle-image"
                  className={`flex items-center justify-between px-3 py-2 border cursor-pointer transition-colors ${
                    genImage
                      ? "border-[#ccff00] bg-[#ccff00]/5"
                      : "border-white/10"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Sparkle size={16} weight="fill" className={genImage ? "text-[#ccff00]" : "text-zinc-500"} />
                    <span className="text-sm">Generate AI images too</span>
                  </div>
                  <Checkbox
                    checked={genImage}
                    onCheckedChange={setGenImage}
                    className="border-white/20 data-[state=checked]:bg-[#ccff00] data-[state=checked]:text-black rounded-none"
                  />
                </label>
                <div className="font-mono text-[10px] text-zinc-500 mt-1.5 leading-relaxed">
                  {scrapedImageUrl
                    ? "Your selected site image will be used as the hero creative."
                    : genImage
                      ? "AI will create 3 aspect ratios (1:1 / 9:16 / 16:9)."
                      : "No creatives will be generated. You can still pick a site image above."}
                </div>
              </div>
              <div>
                <div className="label-mono mb-2">affiliate link (optional)</div>
                <Select value={affiliateLinkId || "none"} onValueChange={(v) => setAffiliateLinkId(v === "none" ? "" : v)}>
                  <SelectTrigger
                    data-testid="link-select"
                    className="rounded-none bg-[#121212] border-white/10 h-11"
                  >
                    <SelectValue placeholder="Attach a saved link" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10 rounded-none">
                    <SelectItem value="none">— none —</SelectItem>
                    {links.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="label-mono mb-2">campaign name · UTM</div>
                <Input
                  data-testid="campaign-input"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  placeholder="e.g. Summer Sale 2026"
                  className="rounded-none bg-[#121212] border-white/10 h-11 font-mono text-sm"
                />
                <div className="font-mono text-[10px] text-zinc-500 mt-1.5 leading-relaxed">
                  Auto-adds utm_source=&lt;platform&gt;, utm_medium=cpc,
                  utm_campaign=&lt;slug&gt;, utm_content=&lt;variant&gt;
                </div>
              </div>
            </div>

            <div className="bg-[#0a0a0a] border border-white/10 p-6 xl:col-span-1" data-testid="compliance-config">
              <div className="flex items-center justify-between mb-3">
                <div className="label-mono">compliance policies</div>
                <ShieldCheck size={14} className="text-[#ccff00]" weight="duotone" />
              </div>
              {policies.length === 0 ? (
                <div className="text-xs text-zinc-500 leading-relaxed">
                  No policies uploaded yet.{" "}
                  <a
                    href="/compliance"
                    className="text-[#ccff00] underline decoration-dotted underline-offset-4"
                  >
                    Add one →
                  </a>
                </div>
              ) : (
                <div className="space-y-1.5 max-h-40 overflow-auto pr-1">
                  {policies.map((p) => {
                    const checked = compliancePolicyIds.includes(p.id);
                    return (
                      <label
                        key={p.id}
                        data-testid={`policy-toggle-${p.id}`}
                        className={`flex items-center gap-2 px-3 py-2 border cursor-pointer transition-colors ${
                          checked
                            ? "border-[#ccff00] bg-[#ccff00]/5 text-[#ccff00]"
                            : "border-white/10 text-zinc-400 hover:border-white/30"
                        }`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => togglePolicy(p.id)}
                          className="border-white/20 data-[state=checked]:bg-[#ccff00] data-[state=checked]:text-black rounded-none"
                        />
                        <span className="text-xs font-medium truncate flex-1">
                          {p.name}
                        </span>
                        <span className="font-mono text-[9px] text-zinc-500">
                          {p.platform_type}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
              <div className="font-mono text-[10px] text-zinc-500 mt-2 leading-relaxed">
                Rules are injected into the LLM at generation time. Post-check
                available on the results.
              </div>
              <div className="mt-4 pt-4 border-t border-white/5">
                <div className="label-mono mb-2">qualifier survey (optional)</div>
                <Select value={surveyId || "none"} onValueChange={(v) => setSurveyId(v === "none" ? "" : v)}>
                  <SelectTrigger
                    data-testid="survey-select"
                    className="rounded-none bg-[#121212] border-white/10 h-10"
                  >
                    <SelectValue placeholder="Attach a survey" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10 rounded-none">
                    <SelectItem value="none">— none —</SelectItem>
                    {surveys.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} · {s.sender_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {surveys.length === 0 && (
                  <div className="text-[10px] text-zinc-500 mt-1.5 font-mono">
                    <a href="/surveys" className="text-[#ccff00] underline decoration-dotted">Create one →</a>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6">
            <button
              data-testid="generate-btn"
              onClick={doGenerate}
              disabled={loadingGen}
              className="bg-[#ccff00] text-black hover:bg-[#b3e600] disabled:opacity-40 font-semibold rounded-none transition-all duration-200 active:scale-95 shadow-[0_0_20px_rgba(204,255,0,0.2)] hover:shadow-[0_0_35px_rgba(204,255,0,0.5)] px-8 py-4 tracking-wide flex items-center gap-3"
            >
              {loadingGen ? (
                <>
                  <Sparkle size={18} weight="fill" className="animate-spin" />
                  Generating ads & creative…
                </>
              ) : (
                <>
                  <Lightning size={18} weight="fill" />
                  Generate {platforms.length} ad{platforms.length !== 1 ? "s" : ""}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Results */}
      {result && result.ads?.length > 0 && (
        <div className="fade-up">
          <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
            <div className="label-mono">03 · results</div>
            <div className="flex items-center gap-2">
              <CostEstimateBadge adSetId={result?.id} />
              <button
                data-testid="open-utm-builder"
                onClick={() => setUtmOpen(true)}
                className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider px-3 py-2 border border-white/10 text-zinc-400 hover:text-[#ccff00] hover:border-[#ccff00]/40 transition-colors"
                title="Customize UTM tags"
              >
                <Tag size={13} weight="fill" />
                UTM
              </button>
              <button
                data-testid="open-geo-targeting"
                onClick={() => setGeoOpen(true)}
                className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider px-3 py-2 border border-white/10 text-zinc-400 hover:text-[#ccff00] hover:border-[#ccff00]/40 transition-colors"
                title="Geo targeting"
              >
                <Globe size={13} weight="fill" />
                GEO
                {result?.geo && (result.geo.countries?.length || result.geo.states?.length || result.geo.cities?.length || result.geo.radii?.length) ? (
                  <span className="w-1.5 h-1.5 bg-[#ccff00] rounded-full" />
                ) : null}
              </button>
              <a
                data-testid="export-csv-btn"
                href={exportCsvUrl(result.id)}
                className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider px-3 py-2 border border-white/10 text-zinc-400 hover:text-[#ccff00] hover:border-[#ccff00]/40 transition-colors"
                title="Download all variants as CSV"
              >
                <FileCsv size={13} weight="fill" />
                CSV
              </a>
              <a
                data-testid="export-zip-btn"
                href={exportZipUrl(result.id)}
                className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider px-3 py-2 border border-white/10 text-zinc-400 hover:text-[#ccff00] hover:border-[#ccff00]/40 transition-colors"
                title="Download bundle (CSV + creatives) as .zip"
              >
                <FileZip size={13} weight="fill" />
                ZIP bundle
              </a>
            </div>
          </div>

          {(result.compliance_policy_ids?.length > 0 || compliancePolicyIds.length > 0) && (
            <div className="mb-6 space-y-3">
              <div
                className="bg-[#0a0a0a] border border-white/10 p-4 flex items-center justify-between gap-4 flex-wrap"
                data-testid="compliance-panel"
              >
                <div className="flex items-center gap-3">
                  <ShieldCheck size={18} className="text-[#ccff00]" weight="duotone" />
                  <div>
                    <div className="font-display font-bold text-sm">
                      Compliance audit
                    </div>
                    <div className="font-mono text-[10px] text-zinc-500">
                      {complianceReport
                        ? `${complianceReport.overall_status.toUpperCase()} · ${complianceReport.total_violations} finding(s) across ${complianceReport.results.length} variants`
                        : `${(compliancePolicyIds.length || result.compliance_policy_ids?.length || 0)} policy(ies) attached · click to run audit`}
                    </div>
                  </div>
                </div>
                <button
                  data-testid="run-compliance-btn"
                  onClick={doComplianceCheck}
                  disabled={checkingCompliance}
                  className="bg-[#ccff00] text-black hover:bg-[#b3e600] disabled:opacity-40 font-semibold px-5 py-2 flex items-center gap-2 transition-colors"
                >
                  {checkingCompliance ? (
                    <Sparkle size={14} weight="fill" className="animate-spin" />
                  ) : (
                    <ShieldCheck size={14} weight="fill" />
                  )}
                  {checkingCompliance ? "Auditing…" : complianceReport ? "Re-audit" : "Run compliance check"}
                </button>
              </div>
              <EnforcementPanel adSet={result} onAdSetUpdate={setResult} />
            </div>
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList
              data-testid="platform-tabs"
              className="rounded-none bg-transparent border border-white/10 p-1 flex flex-wrap h-auto"
            >
              {result.ads.map((a) => (
                <TabsTrigger
                  key={a.platform}
                  value={a.platform}
                  data-testid={`tab-${a.platform}`}
                  className="rounded-none data-[state=active]:bg-[#ccff00] data-[state=active]:text-black font-mono text-xs uppercase tracking-wider px-4 py-2"
                >
                  {a.platform}
                </TabsTrigger>
              ))}
            </TabsList>
            {result.ads.map((a) => {
              const perfForPlatform = performance.find((p) => p.platform === a.platform);
              const complianceForPlatform = complianceReport?.results?.filter(
                (r) => r.platform === a.platform,
              );
              return (
                <TabsContent key={a.platform} value={a.platform} className="mt-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <AdCard
                      ad={a}
                      creatives={creatives}
                      url={result.url}
                      adSetId={result.id}
                      performance={perfForPlatform}
                      complianceResults={complianceForPlatform}
                      tone={tone}
                      audience={audience}
                      onAdSetUpdate={setResult}
                    />
                    <div className="flex flex-col gap-6">
                      <ABLeaderboard
                        performance={performance}
                        activePlatform={a.platform}
                        adSetId={result.id}
                        tone={tone}
                        audience={audience}
                        onAdSetUpdate={setResult}
                      />
                      <div className="bg-[#0a0a0a] border border-white/10 p-6">
                        <div className="label-mono mb-3">how A/B testing works</div>
                        <ul className="text-sm text-zinc-300 space-y-2 leading-relaxed">
                          <li>· 3 headline+hook angles per platform (curiosity / benefit / social proof).</li>
                          <li>· Copy the per-variant tracker URL (link icon) — UTM + <code className="text-[#ccff00] font-mono text-xs">af_click_id</code> auto-appended.</li>
                          <li>· Clicks stream in live — polling every 6s.</li>
                          <li>· Drop the pixel or postback conversions → variants rank by <span className="text-cyan-300">CVR</span> &amp; <span className="text-pink-400">revenue</span> too.</li>
                          <li>· At 30+ clicks with clear leader, chi-square hits 95% confidence &amp; the auto-caller unlocks.</li>
                          <li>· CTA &ldquo;{a.cta}&rdquo; stays constant across variants for a fair test.</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        </div>
      )}

      <UtmBuilderDialog
        open={utmOpen}
        onOpenChange={setUtmOpen}
        adSetId={result?.id}
      />
      <GeoTargetingDialog
        open={geoOpen}
        onOpenChange={setGeoOpen}
        adSetId={result?.id}
        initialGeo={result?.geo}
        onSaved={() => {
          if (result?.id) {
            getAdSet(result.id).then(setResult).catch(() => {});
          }
        }}
      />
    </div>
  );
}
